import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";

import { AuthProvider } from "./contexts/AuthContext";
import { ProfileProvider } from "./components/Profile/ProfileContext";
import { FavoritesProvider } from "./contexts/FavoritesContext";
import { AuthModalProvider } from "./contexts/AuthModalContext";
import { CartProvider } from "./contexts/CartContext";
import { OrderNotificationsProvider } from "./contexts/OrderNotificationsContext";
import WelcomeToast from "./components/core/WelcomeToast";
import { useAuth } from "./contexts/AuthContext";
import { useProfile } from "./components/Profile/ProfileContext";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import RouteMetadata from "./components/seo/RouteMetadata";
import { GoogleOAuthWrapper } from "./contexts/GoogleOAuthWrapper";

// Eagerly loaded: homepage + catalog (critical path) + route guards
import HomePage from "./pages/HomePage";
import CatalogPage from "./pages/CatalogPage";
import RequireAdmin from "./routes/RequireAdmin";
import RequireCommissionOwner from "./routes/RequireCommissionOwner";
import RequireLoggedIn from "./routes/RequireAuth";
import AdminLayout from "./components/Admin/AdminLayout";
import "./App.css";

// Lazy loaded: everything else
const InfoPage = React.lazy(() => import("./pages/InfoPage"));
const FaqPage = React.lazy(() => import("./pages/FaqPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const TermsPage = React.lazy(() => import("./pages/TermsPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const CookiePolicyPage = React.lazy(() => import("./pages/CookiePolicyPage"));
const RefundPolicyPage = React.lazy(() => import("./pages/RefundPolicyPage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const ReportBugPage = React.lazy(() => import("./pages/ReportBugPage"));
const SellPage = React.lazy(() => import("./pages/SellPage"));
const BoostSelectionPage = React.lazy(() => import("./pages/BoostSelectionPage"));
const BoostPaymentPage = React.lazy(() => import("./pages/BoostPaymentPage"));
const BoostConfirmationPage = React.lazy(() => import("./pages/BoostConfirmationPage"));
const UploadGuidelines = React.lazy(() => import("./pages/UploadGuidelines"));
const ShirtSellingPage = React.lazy(() => import("./pages/ShirtSellingPage/ShirtSellingPage"));
const ShirtAuctionPage = React.lazy(() => import("./pages/ShirtAuctionPage/ShirtAuctionPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const CartPage = React.lazy(() => import("./pages/CartPage"));
const PaymentPage = React.lazy(() => import("./pages/PaymentPage"));
const PaymentInstructionsPage = React.lazy(() => import("./pages/PaymentInstructionsPage"));
const OrderConfirmationPage = React.lazy(() => import("./pages/OrderConfirmationPage"));
const AuctionHistoryPage = React.lazy(() => import("./pages/AuctionHistoryPage"));
const MyOrdersPage = React.lazy(() => import("./pages/MyOrdersPage"));
const AuctionCheckoutPage = React.lazy(() => import("./pages/AuctionCheckoutPage"));
const ShirtModerationPage = React.lazy(() => import("./pages/admin/ShirtModerationPage"));
const AdminShirtListPage = React.lazy(() => import("./pages/admin/AdminShirtListPage"));
const AdminProblemPage = React.lazy(() => import("./pages/admin/AdminProblemPage"));
const AdminDisputesPage = React.lazy(() => import("./pages/admin/AdminDisputesPage"));
const AdminDisputeDetailPage = React.lazy(() => import("./pages/admin/AdminDisputeDetailPage"));
const UserModerationPage = React.lazy(() => import("./pages/admin/UserModerationPage"));
const CommissionDashboardPage = React.lazy(() => import("./pages/admin/CommissionDashboardPage"));
const ChatsLayout = React.lazy(() => import("./pages/chats/ChatsLayout"));
const ChatsInboxPage = React.lazy(() => import("./pages/chats/ChatsInboxPage"));
const ChatsNewPage = React.lazy(() => import("./pages/chats/ChatsNewPage"));
const SettingsLayout = React.lazy(() => import("./pages/Settings/SettingsLayout"));
const PublicProfilePage = React.lazy(() => import("./pages/PublicProfilePage"));
const SellerProfilePage = React.lazy(() => import("./pages/SellerProfilePage"));
const ProfileSettings = React.lazy(() => import("./pages/Settings/pages/ProfileSettings"));
const AccountSettings = React.lazy(() => import("./pages/Settings/pages/AccountSettings"));
const PaymentSettings = React.lazy(() => import("./pages/Settings/pages/PaymentSettings"));
const ShippingSettings = React.lazy(() => import("./pages/Settings/pages/ShippingSettings"));
const PrivacySettings = React.lazy(() => import("./pages/Settings/pages/PrivacySettings"));
const PreferencesSettings = React.lazy(() => import("./pages/Settings/pages/PreferencesSettings"));

function ScrollToTop() {
  const { pathname, search } = useLocation();

  React.useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const scrollNow = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      const main = document.querySelector("main");
      if (main) {
        main.scrollTop = 0;
      }
    };

    scrollNow();
    requestAnimationFrame(scrollNow);
    setTimeout(scrollNow, 0);
  }, [pathname, search]);

  return null;
}

function Layout() {
  const { isAuthed } = useAuth();
  const { profile } = useProfile();
  const location = useLocation();

  const [toast, setToast] = React.useState({
    open: false,
    title: "",
    message: "",
  });

  const prevAuthed = React.useRef(isAuthed);

  React.useEffect(() => {
    prevAuthed.current = isAuthed;
  }, [isAuthed, profile?.username]);

  const hideFooter =
    location.pathname === "/payment" ||
    location.pathname.startsWith("/betaal") ||
    location.pathname.includes("/checkout");

  return (
    <div className="site-layout">
      <div className="site-topbar" />
      <Header />

      <WelcomeToast
        open={toast.open}
        title={toast.title}
        message={toast.message}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />

      <main className="site-main">
        <Suspense fallback={<div className="smc-loading">Loading…</div>}>
          <Outlet />
        </Suspense>
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <GoogleOAuthWrapper>
      <AuthProvider>
        <AuthModalProvider>
          <ProfileProvider>
            <CartProvider>
              <OrderNotificationsProvider>
              <FavoritesProvider>
                <Router>
                  <ScrollToTop />
                  <RouteMetadata />

                  <Routes>
                    <Route element={<Layout />}>
                    {/* Home / Catalog */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/catalog" element={<CatalogPage />} />

                    {/* Public */}
                    <Route path="/shirt/:id" element={<ShirtSellingPage />} />
                    <Route
                      path="/shirt/auction/:id"
                      element={<ShirtAuctionPage />}
                    />
                    <Route path="/user/:username" element={<PublicProfilePage />} />
                    <Route path="/users/:userId" element={<SellerProfilePage />} />
                    <Route
                      path="/upload-guidelines"
                      element={<UploadGuidelines />}
                    />
                    <Route path="/info" element={<InfoPage />} />
                    <Route path="/faq" element={<FaqPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/cookies" element={<CookiePolicyPage />} />
                    <Route path="/refund" element={<RefundPolicyPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/bug" element={<ReportBugPage />} />

                    {/* Logged in */}
                    <Route element={<RequireLoggedIn />}>
                      <Route path="/sell" element={<SellPage />} />
                      <Route
                        path="/sell/boost"
                        element={<BoostSelectionPage />}
                      />
                      <Route
                        path="/sell/boost/betaling"
                        element={<BoostPaymentPage />}
                      />
                      <Route
                        path="/boost-bevestigd"
                        element={<BoostConfirmationPage />}
                      />

                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/favorites" element={<AuctionHistoryPage />} />
                      <Route path="/auction-history" element={<Navigate to="/favorites" replace />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/payment" element={<PaymentPage />} />
                      <Route
                        path="/betaalinstructies"
                        element={<PaymentInstructionsPage />}
                      />
                      <Route
                        path="/bestelling-bevestigd"
                        element={<OrderConfirmationPage />}
                      />
                      <Route path="/orders" element={<MyOrdersPage />} />
                      <Route path="/orders/:orderId" element={<MyOrdersPage />} />
                      <Route
                        path="/auction/:auctionId/checkout"
                        element={<AuctionCheckoutPage />}
                      />

                      <Route path="/settings" element={<SettingsLayout />}>
                        <Route
                          index
                          element={<Navigate to="profile" replace />}
                        />
                        <Route path="profile" element={<ProfileSettings />} />
                        <Route path="account" element={<AccountSettings />} />
                        <Route path="shipping" element={<ShippingSettings />} />
                        <Route path="payments" element={<PaymentSettings />} />
                        <Route path="privacy" element={<PrivacySettings />} />
                        <Route
                          path="preferences"
                          element={<PreferencesSettings />}
                        />
                      </Route>

                      <Route path="/chats" element={<ChatsLayout />}>
                        <Route index element={<ChatsInboxPage />} />
                        <Route path="new" element={<ChatsNewPage />} />
                      </Route>
                    </Route>

                    {/* Admin */}
                    <Route element={<RequireAdmin />}>
                      <Route element={<AdminLayout />}>
                        <Route
                          path="/admin/shirts"
                          element={<ShirtModerationPage />}
                        />
                        <Route
                          path="/admin/shirts/:id"
                          element={<ShirtModerationPage />}
                        />
                        <Route
                          path="/admin/shirt-list"
                          element={<AdminShirtListPage />}
                        />
                        <Route
                          path="/admin/users"
                          element={<UserModerationPage />}
                        />
                        <Route
                          path="/admin/problem-reports/:orderId"
                          element={<AdminProblemPage />}
                        />
                        <Route
                          path="/admin/disputes"
                          element={<AdminDisputesPage />}
                        />
                        <Route
                          path="/admin/disputes/:disputeId"
                          element={<AdminDisputeDetailPage />}
                        />
                      </Route>
                    </Route>

                    {/* Commissie-dashboard: alleen voor de contract-eigenaar
                        (eigen guard i.p.v. RequireAdmin — e-mailcheck) */}
                    <Route element={<RequireCommissionOwner />}>
                      <Route
                        path="/admin/commissie"
                        element={<CommissionDashboardPage />}
                      />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
                </Router>
            </FavoritesProvider>
              </OrderNotificationsProvider>
          </CartProvider>
        </ProfileProvider>
      </AuthModalProvider>
    </AuthProvider>
    </GoogleOAuthWrapper>
  );
}
